import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BookIcon from '@mui/icons-material/Book';
import { AuthContext } from '../../providers/AuthProvider';
import { useMyPatterns } from '../../hooks/useMyPatterns';
import { TopBar } from '../Main/Shared/TopBar';
import {
	FIREBASE_STORAGE_PATTERN_LIBRARY_URL,
	MEASUREMENT_UNIT,
	ROUTES,
} from '../../config/constants';
import {
	getHeightCM,
	getHeightInchPrimary,
	getHeightInchFraction,
} from '../../utils/numbers';
import {
	COLOR_BROWN_PRIMARY,
	GRADIENT_LIBRARY_INFO_BG,
	SHADOW_CARD_SUBTLE,
	RADIUS_SM,
} from '../../styles/tokens';

const formatDate = (timestamp) => {
	if (!timestamp) return '';
	const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
	return date.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
};

const libraryThumbnailUrl = (fileName) =>
	`${FIREBASE_STORAGE_PATTERN_LIBRARY_URL}${encodeURIComponent(fileName)}.jpg?alt=media`;

const PatternRow = ({ pattern }) => {
	const isLibrary = pattern.type === 'library' && pattern.fileName;

	return (
		<Card
			sx={{
				display: 'flex',
				flexDirection: { xs: 'column', sm: 'row' },
				alignItems: { xs: 'stretch', sm: 'center' },
				gap: 2,
				p: 3,
				boxShadow: SHADOW_CARD_SUBTLE,
				borderRadius: RADIUS_SM,
			}}
		>
			{/* Thumbnail + Info — always side by side within this row */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 3,
					flex: 1,
					minWidth: 0,
				}}
			>
				{/* Thumbnail */}
				<Box
					sx={{
						width: 120,
						height: 120,
						borderRadius: RADIUS_SM,
						overflow: 'hidden',
						flexShrink: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'grey.100',
					}}
				>
					{isLibrary ? (
						<img
							src={libraryThumbnailUrl(pattern.fileName)}
							alt={pattern.fileName}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}}
						/>
					) : pattern.tempImageURL ? (
						<img
							src={pattern.tempImageURL}
							alt="Custom pattern"
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}}
						/>
					) : (
						<BookIcon sx={{ fontSize: 60, color: 'grey.400' }} />
					)}
				</Box>

				{/* Info */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="h4"
						sx={{ color: COLOR_BROWN_PRIMARY }}
					>
						{isLibrary
							? (pattern.libraryName ?? pattern.fileName).replace(
									/\b\w/g,
									(c) => c.toUpperCase(),
								)
							: 'Custom pattern'}
					</Typography>
					<Typography
						variant="body1"
						sx={{
							color: 'text.secondary',
							fontSize: '1.1rem',
							mt: 0.5,
						}}
					>
						{isLibrary ? 'Library pattern' : 'Your image'} &middot;{' '}
						{formatDate(pattern.purchasedAt)}
					</Typography>
					{pattern.bookParams && (
						<Typography
							variant="body1"
							sx={{
								color: 'text.secondary',
								fontSize: '1.1rem',
								mt: 0.5,
							}}
						>
							{(() => {
								const {
									height,
									measurementUnit,
									lastOddPage,
									angleLine,
									isInkSaver,
								} = pattern.bookParams;
								let heightLabel;
								if (measurementUnit === MEASUREMENT_UNIT.inch) {
									const fraction =
										getHeightInchFraction(height);
									const fractionPart =
										fraction.label !== '-'
											? ` ${fraction.label}`
											: '';
									heightLabel = `${getHeightInchPrimary(height)}${fractionPart}"`;
								} else {
									heightLabel = `${getHeightCM(height)}cm`;
								}
								return [
									`${heightLabel} tall`,
									`${lastOddPage} pages`,
									angleLine && angleLine !== 'None'
										? `${angleLine} angle line`
										: null,
									isInkSaver ? 'Ink saver' : null,
								]
									.filter(Boolean)
									.join(' · ');
							})()}
						</Typography>
					)}
				</Box>
			</Box>

			{/* Download */}
			<Button
				variant="contained"
				href={pattern.downloadURL}
				target="_blank"
				rel="noopener noreferrer"
				sx={{
					lineHeight: 1,
					fontSize: '1.1rem',
					px: 3,
					pt: 1.5,
					pb: 1,
					flexShrink: 0,
					alignSelf: { xs: 'stretch', sm: 'center' },
				}}
			>
				Download
			</Button>
		</Card>
	);
};

export const MyPatternsPage = () => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const { patterns, loading, error } = useMyPatterns();

	if (user === undefined) {
		return (
			<Stack alignItems="center" sx={{ mt: 4 }}>
				<CircularProgress />
			</Stack>
		);
	}

	if (user === null) {
		navigate(ROUTES.ABOUT);
		return null;
	}

	return (
		<>
			<TopBar />

			<Container sx={{ pb: 6 }}>
				<Box
					sx={{
						background: GRADIENT_LIBRARY_INFO_BG,
						borderRadius: '16px',
						padding: { xs: '20px', md: '32px' },
						maxWidth: '900px',
						margin: '30px auto',
						marginTop: '18px',
						boxShadow: SHADOW_CARD_SUBTLE,
						textAlign: 'center',
					}}
				>
					<h2
						style={{
							fontSize: '3.5rem',
							fontWeight: 600,
							margin: 0,
							marginTop: '-10px',
							letterSpacing: '0.05em',
							lineHeight: 1.1,
						}}
					>
						<span
							style={{
								display: 'inline-block',
								background:
									'linear-gradient(90deg, #00b4a6 0%, #3b82f6 50%, #8b5cf6 100%)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
								backgroundClip: 'text',
								color: 'transparent',
							}}
						>
							My Patterns
						</span>
					</h2>
					<Typography
						variant="h5"
						sx={{
							color: COLOR_BROWN_PRIMARY,
							fontWeight: 400,
							mt: 1,
						}}
					>
						All the patterns you've generated - download them any
						time.
					</Typography>
				</Box>

				{loading ? (
					<Stack alignItems="center" sx={{ my: 4 }}>
						<CircularProgress />
					</Stack>
				) : error ? (
					<Typography
						sx={{ textAlign: 'center', my: 4, color: 'error.main' }}
					>
						{error}
					</Typography>
				) : patterns.length === 0 ? (
					<Typography
						variant="h5"
						sx={{
							textAlign: 'center',
							my: 6,
							color: 'text.secondary',
						}}
					>
						You haven't generated any patterns yet.
					</Typography>
				) : (
					<Stack spacing={2} sx={{ maxWidth: 800, mx: 'auto' }}>
						{patterns.map((pattern) => (
							<PatternRow key={pattern.id} pattern={pattern} />
						))}
					</Stack>
				)}
			</Container>
		</>
	);
};
